/**
 * GenericCtaSection — a call-to-action band with heading, text, and buttons.
 *
 * Props:
 *   heading   string
 *   text      string
 *   alignment 'center'|'left'|'right'   default 'center'
 *   buttons   Array<{ id, label, href }>
 *
 * Renders nothing when heading, text, and buttons are all empty.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Container, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RevealOnScroll from '../../components/RevealOnScroll';

const ALIGN_MAP = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

const TEXT_ALIGN_MAP = {
  left: 'left',
  center: 'center',
  right: 'right',
};

export default function GenericCtaSection({ heading, text, alignment, buttons }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const safeButtons = Array.isArray(buttons) ? buttons : [];
  const hasContent = heading || text || safeButtons.some(b => b.label && b.href);
  if (!hasContent) return null;

  const alignItems = ALIGN_MAP[alignment] || 'center';
  const textAlign = TEXT_ALIGN_MAP[alignment] || 'center';

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 7, md: 11 },
        bgcolor: isDark
          ? 'rgba(27,54,93,0.22)'
          : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Container maxWidth="md">
        <RevealOnScroll>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems,
              textAlign,
              gap: 2.5,
            }}
          >
            {heading && (
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.75rem', md: '2.4rem' },
                  lineHeight: 1.2,
                  color: 'text.primary',
                  letterSpacing: '-0.02em',
                  maxWidth: 680,
                }}
              >
                {heading}
              </Typography>
            )}

            {text && (
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.75,
                  maxWidth: 600,
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  whiteSpace: 'pre-line',
                }}
              >
                {text}
              </Typography>
            )}

            {safeButtons.some(b => b.label && b.href) && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  flexWrap: 'wrap',
                  justifyContent: alignItems,
                  mt: 0.5,
                }}
              >
                {safeButtons.map((btn, idx) =>
                  btn.label && btn.href ? (
                    <Button
                      key={btn.id || idx}
                      variant={idx === 0 ? 'contained' : 'outlined'}
                      size="large"
                      href={btn.href}
                      sx={{
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        fontSize: '1rem',
                        minHeight: 48,
                      }}
                    >
                      {btn.label}
                    </Button>
                  ) : null
                )}
              </Box>
            )}
          </Box>
        </RevealOnScroll>
      </Container>
    </Box>
  );
}

GenericCtaSection.propTypes = {
  heading: PropTypes.string,
  text: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      href: PropTypes.string,
    })
  ),
};

GenericCtaSection.defaultProps = {
  alignment: 'center',
  buttons: [],
};
